import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import TopNav from "../components/TopNav";
import Footer from "../components/Footer";
import { ArtifactGallery } from "../components/ArtifactGallery";
import API from "../lib/api";
import logger from "../lib/logger";
import { ArrowLeft } from "lucide-react";

export default function CollectionItem() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get(`/public/collection/${id}`);
        setItem(data);
      } catch (e) {
        logger.error("Collection item load failed:", e);
        setNotFound(true);
      }
    })();
  }, [id]);

  if (notFound) return (
    <div className="min-h-screen bg-cream flex flex-col">
      <TopNav />
      <div className="max-w-4xl mx-auto px-6 py-20 text-center flex-1">
        <div className="font-display text-4xl text-navy mb-2">Item not found</div>
        <p className="text-ink-soft mb-4">This item may not be published or has been removed.</p>
        <Link to="/collection" className="btn-outline">Back to Collection</Link>
      </div>
      <Footer />
    </div>
  );

  if (!item) return (
    <div className="min-h-screen bg-cream flex flex-col"><TopNav /><div className="flex-1 flex items-center justify-center text-muted-h">Loading…</div></div>
  );

  const artifacts = (item.file_ids || []).map((fid) => ({ file_id: fid, file_name: "Heritage image" }));

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <TopNav />
      <article className="max-w-4xl mx-auto px-6 py-14 flex-1 w-full">
        <Link to="/collection" className="text-navy hover:underline text-sm inline-flex items-center gap-1 mb-6" data-testid="back-link">
          <ArrowLeft size={14} /> Back to Collection
        </Link>
        <div className="text-xs uppercase tracking-widest text-gold mb-3">{item.category}</div>
        <h1 className="font-display text-5xl md:text-6xl text-navy mb-4 leading-tight" data-testid="collection-item-title">{item.title}</h1>
        <div className="text-ink-soft text-sm mb-8 flex flex-wrap gap-4">
          {item.relevant_dates && <span><strong className="text-ink">Date:</strong> {item.relevant_dates}</span>}
          {item.location && <span><strong className="text-ink">Location:</strong> {item.location}</span>}
        </div>

        {artifacts.length > 0 && (
          <div className="mb-10">
            <ArtifactGallery artifacts={artifacts} canDownload={false} />
          </div>
        )}

        <div className="ornament"><span>✦</span></div>

        {item.description && (
          <section className="mb-10">
            <h2 className="font-display text-3xl text-navy mb-3">Description</h2>
            <p className="text-lg text-ink-soft leading-relaxed">{item.description}</p>
          </section>
        )}
        {item.historical_context && (
          <section className="mb-10">
            <h2 className="font-display text-3xl text-navy mb-3">Historical Context</h2>
            <p className="text-lg text-ink-soft leading-relaxed whitespace-pre-wrap">{item.historical_context}</p>
          </section>
        )}
        {item.tags?.length > 0 && (
          <section className="mb-10">
            <h3 className="text-xs uppercase tracking-widest text-muted-h mb-3">Tags</h3>
            <div className="flex flex-wrap gap-2">
              {item.tags.map((t) => (
                <Link key={t} to={`/collection?tag=${encodeURIComponent(t)}`} className="text-sm px-3 py-1 rounded-full bg-parchment text-ink-soft hover:bg-[var(--oih-navy)] hover:text-white transition">
                  {t}
                </Link>
              ))}
            </div>
          </section>
        )}
        {item.attribution && (
          <div className="border-t border-heritage pt-6 mt-10 text-sm text-muted-h italic">{item.attribution}</div>
        )}
      </article>
      <Footer />
    </div>
  );
}
